import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Button,
  ButtonGroup,
  TextContainer,
  Heading,
  Spinner,
  ProgressBar,
  Banner,
  Checkbox,
  Text
} from '@shopify/polaris';
import { useAuth } from '../../contexts/AuthContext';
import { useTemplate } from '../../contexts/TemplateContext';
import { useShop } from '../../contexts/ShopContext';
import { ImageSelector } from './ImageSelector';
import ResultPreviewPanel from './ResultPreviewPanel';
import { PersistentResultsSidebar } from './PersistentResultsSidebar';
import { getAIModelById } from '../../services/aiModelService';
import { deductCredits } from '../../services/optimizedCreditService';
import { getOpenAIApiKey, getProviderApiKey } from '../../services/apiKeyService';

/**
 * Component for executing Image Analyzing templates - Simplified Version
 * This version only supports:
 * 1. Featured image or manual selection
 * 2. Output to sidebar or append to description
 */
export const ImageTemplateExecutor = ({
  template,
  products,
  onUpdate,
  onSaveResults,
  onClose,
  onProgress // New prop for reporting progress to parent
}) => {
  const { user } = useAuth();
  const { currentShop } = useShop();
  const { handleTemplateUpdate } = useTemplate();
  
  // State for image selection and processing
  const [selectedImages, setSelectedImages] = useState({});
  const [error, setError] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [useFeaturedImages, setUseFeaturedImages] = useState(template?.useFeaturedImage === true);

  // Load the AI model details and initialize featured images if needed
  useEffect(() => {
    const loadModel = async () => {
      if (template?.modelId) {
        try {
          const model = await getAIModelById(template.modelId);
          setSelectedModel(model);
        } catch (error) {
          console.error('Error loading AI model:', error);
          setError('Failed to load AI model configuration');
        }
      }
    };
    
    loadModel();
    
    // Log the template configuration for debugging
    console.log('Template configuration loaded:', {
      id: template?.id,
      name: template?.name,
      sidebarOnly: template?.sidebarOnly,
      useFeaturedImage: template?.useFeaturedImage
    });
    
    // If template has useFeaturedImage set to true, select featured images immediately
    if (template?.useFeaturedImage === true) {
      console.log('Template has useFeaturedImage set to true, auto-selecting featured images');
      setUseFeaturedImages(true);
      // We'll select the images in another useEffect that depends on products
    }
  }, [template]);

  // Auto-select featured images when products change and useFeaturedImages is true
  useEffect(() => {
    if (useFeaturedImages && products.length > 0) {
      console.log('Auto-selecting featured images because useFeaturedImages is true');
      const success = selectFeaturedImages();
      
      // If this is a template with useFeaturedImage set to true, automatically process the images
      if (template?.useFeaturedImage === true) {
        console.log('Template has useFeaturedImage=true, automatically processing images');
        // Use a small timeout to ensure the images are selected first
        setTimeout(() => {
          // Clear any error messages before processing
          setError('');
          processImages();
        }, 500);
      }
    }
  }, [useFeaturedImages, products, template]);

  // Handle selecting an image for a product
  const handleSelectImage = (productId, image) => {
    setSelectedImages(prev => ({
      ...prev,
      [productId]: [image] // Store as array for consistency with multiple image selection
    }));
  };

  // Handle selecting multiple images for a product
  const handleSelectImages = (productId, images) => {
    setSelectedImages(prev => ({
      ...prev,
      [productId]: images
    }));
  };

  // Check if all products have selected images
  const allProductsHaveImages = useCallback(() => {
    // If using featured images, we don't need to manually select images
    if (useFeaturedImages) {
      return true;
    }
    
    // Otherwise, check if all products have selected images (at least one image per product)
    return products.every(product => {
      const productImages = selectedImages[product.id];
      return productImages && (Array.isArray(productImages) ? productImages.length > 0 : productImages);
    });
  }, [products, selectedImages, useFeaturedImages]);

  // Check if we should auto-process images
  const shouldAutoProcess = useCallback(() => {
    return template?.useFeaturedImage === true && useFeaturedImages;
  }, [template, useFeaturedImages]);

  // Auto-select featured images for all products
  const selectFeaturedImages = useCallback(() => {
    console.log('Selecting featured images for products:', products.length);
    const newSelectedImages = {};
    let allProductsHaveImages = true;
    
    products.forEach(product => {
      // Find the featured image or the first image
      const featuredImage = product.images?.find(img => img.position === 1) || product.images?.[0];
      
      if (featuredImage) {
        // Store as array for consistency with multiple image selection
        newSelectedImages[product.id] = [featuredImage];
        console.log(`Selected featured image for product ${product.id}:`, {
          url: featuredImage.url,
          position: featuredImage.position
        });
      } else {
        console.warn(`No images found for product ${product.id}`);
        allProductsHaveImages = false;
      }
    });
    
    // Update the selected images state
    setSelectedImages(newSelectedImages);
    
    // If not all products have images, show an error
    if (!allProductsHaveImages) {
      setError('Some products do not have images. Please add images to all products or remove products without images.');
    }
    
    // Clear any error messages if we successfully selected images for all products
    if (allProductsHaveImages && template?.useFeaturedImage) {
      setError('');
    }
    
    return allProductsHaveImages;
  }, [products, template]);

  // Process the selected images with the AI model
  const processImages = async () => {
    // Validate prerequisites - skip validation if we're auto-processing with featured images
    if (!shouldAutoProcess() && !allProductsHaveImages()) {
      setError('Please select images for all products');
      return;
    }
    
    // Validate that we have a selected model
    if (!selectedModel) {
      setError('No AI model selected. Please try again or contact support.');
      return;
    }
    
    // Log user info for debugging
    console.log('Processing images with user:', user?.uid);
    
    // Start processing
    setError('');
    
    // If using featured images, select them automatically
    if (useFeaturedImages) {
      selectFeaturedImages();
    }
    
    // Calculate total credit cost - simplified to just count the number of products
    const totalProducts = products.length;
    const costPerOutput = selectedModel.creditCostPerOutput || 1;
    const totalCreditCost = totalProducts * costPerOutput;
    
    // Notify parent of initial progress (0%)
    if (onProgress) {
      onProgress(0);
    }
    
    // Close the modal immediately after starting processing
    // This allows the parent component to handle the processing UI
    if (onClose) {
      onClose();
    }
    
    // Continue processing in the background after modal is closed
    // We need to call processSelectedImages directly to ensure it runs
    setTimeout(async () => {
      try {
        await processSelectedImages(totalProducts, totalCreditCost);
      } catch (error) {
        console.error('Error processing images after modal close:', error);
        // Make sure parent knows processing is complete even if there was an error
        if (onProgress) {
          // Ensure progress is exactly 100% when complete
          onProgress(100);
          console.log('Image template processing complete, setting progress to exactly 100%');
        }
      }
    }, 100);
  };
  
  // Process the selected images and report progress
  const processSelectedImages = async (totalProductCount, totalCredits) => {
    const newResults = [];
    
    try {
      // Initial progress report
      if (onProgress) {
        onProgress(5); // Start at 5% to show immediate feedback
      }
      
      // Log the template's sidebarOnly setting for debugging
      console.log('Template sidebarOnly setting:', template.sidebarOnly);
      
      // Prepare execution metadata for credit tracking
      const executionMetadata = {
        templateId: template.id,
        templateName: template.name,
        modelId: selectedModel.id,
        modelName: selectedModel.displayName,
        productCount: totalProductCount,
        creditCost: totalCredits,
        shopId: currentShop?.id
      };
      
      // Deduct credits before processing
      await deductCredits(user?.uid, totalCredits, executionMetadata);
      
      // Process each product
      const totalProducts = products.length; // Define locally for progress calculation
      for (let i = 0; i <totalProducts; i++) {
        const product = products[i];
        const images = selectedImages[product.id];
        
        if (!images) {
          throw new Error(`No images selected for product ${product.title || product.id}`);
        }
        
        try {
          // Replace any placeholders in the prompt with product data
          let processedPrompt = template.prompt;
          
          Object.entries(product).forEach(([key, value]) => {
            if (typeof value === 'string') {
              processedPrompt = processedPrompt.replace(new RegExp(`\{${key}\}`, 'g'), value);
            }
          });
          
          // Get all outputs (fields and metafields) for structured response
          const allOutputs = [...(template.outputFields || [])];
          
          // Add metafields to allOutputs if any
          if (template.outputMetafields && template.outputMetafields.length > 0) {
            template.outputMetafields.forEach(metafieldItem => {
              // Convert object format to string format if needed
              let metafieldId = '';
              
              if (typeof metafieldItem === 'string') {
                metafieldId = metafieldItem;
              } else if (typeof metafieldItem === 'object' && metafieldItem !== null) {
                if (metafieldItem.namespace && metafieldItem.key) {
                  metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                  console.log(`Converted metafield object to string format: ${metafieldId}`);
                }
              }
              
              if (metafieldId && !allOutputs.includes(metafieldId)) {
                allOutputs.push(metafieldId);
              }
            });
          }
          
          // Format the outputs for the prompt
          const formattedOutputs = allOutputs.map(field => {
            // For metafields, use the full path
            return field;
          });
          
          console.log('Formatted output fields for prompt:', formattedOutputs);
          
          // Add instructions for structured output
          // Use the actual field names in the example to avoid confusion
          const exampleField = formattedOutputs[0] || 'field_name';
          
          // Create a clear, explicit prompt with properly formatted field names
          let fieldsList = '';
          formattedOutputs.forEach(field => {
            fieldsList += `- "${field}"
`;
          });
          
          // Combine the user's original prompt with explicit formatting instructions
          const enhancedPrompt = `${processedPrompt}

IMPORTANT FORMATTING INSTRUCTIONS:
Respond with a JSON object that has EXACTLY this structure:
{
  "${formattedOutputs[0]}":"A single plain text string with all the information combined"
}

Your response MUST be a single text string inside the JSON value, NOT a nested object or array. All information should be combined into ONE CONTINUOUS TEXT STRING.

Example of CORRECT format:
{ "${formattedOutputs[0]}": "White cropped cardigan with long bell sleeves and front tie closure, made of ribbed knit fabric. Casual style with slightly open front. Paired with high waist denim jeans with tie belt. Accessories include black shoulder bag and gold jewelry." }

Example of INCORRECT format:
{ "${formattedOutputs[0]}": { "product_type": "Women's cropped cardigan", "color": "White" } }`;
          
          console.log('Requesting structured JSON with fields:', formattedOutputs);
          
          // Prepare the messages with system and user content
          const messages = [
            {
              role: 'system',
              content: 'You are an AI assistant that generates product content for an e-commerce platform based on images. Follow the user\'s prompt exactly to determine what information to extract from the image. Do not make assumptions about what information is needed unless specified in the prompt.'
            },
            {
              role: 'user',
              content: [
                // Add all selected images to the message
                ...images.map(image => ({
                  type: 'image_url',
                  image_url: {
                    url: image.url,
                    detail: 'high'
                  }
                })),
                {
                  type: 'text',
                  text: enhancedPrompt
                }
              ]
            }
          ];
          
          // Prepare the request data for the AI provider service
          const requestData = {
            messages,
            temperature: 0.7,
            max_tokens: 2000,
            images: images.map(image => image.url),
            modelType: 'vision'
          };
          
          console.log('Making AI provider service call:', {
            modelId: selectedModel.id,
            provider: selectedModel.provider,
            messageCount: messages.length,
            imageCount: images.length,
            imageUrls: images.map(img => img.url.substring(0, 50) + '...'),
            prompt: processedPrompt.substring(0, 50) + (processedPrompt.length > 50 ? '...' : '')
          });
          
          // Use centralized AI service for better rate limiting
          let jsonResponse;
          
          if (selectedModel.provider === 'openai') {
            // OpenAI direct API call
            const apiKey = await getOpenAIApiKey();
            if (!apiKey) {
              throw new Error('OpenAI API key is required for image analysis');
            }
            
            const requestPayload = {
              model: selectedModel.modelId || 'gpt-4-vision-preview',
              messages,
              temperature: 0.7,
              max_tokens: 2000
            };
            
            const response = await fetch('https://api.openai.com/v1/chat/completions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
              },
              body: JSON.stringify(requestPayload)
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(`OpenAI API request failed: ${response.status} ${response.statusText}`);
            }
            
            jsonResponse = await response.json();
            
          } else if (selectedModel.provider === 'gemini') {
            // Use centralized AI service for Gemini (better rate limiting)
            const { generateContent } = await import('../../services/aiProviderService');
            
            console.log('Using centralized AI service for Gemini vision');
            console.log('Selected model for debugging:', {
              id: selectedModel.id,
              modelId: selectedModel.modelId,
              internalName: selectedModel.internalName,
              provider: selectedModel.provider
            });
            
            // Use the correct model identifier - try id first, then modelId, then internalName
            const modelIdentifier = selectedModel.id || selectedModel.modelId || selectedModel.internalName;
            if (!modelIdentifier) {
              throw new Error('No valid model identifier found in selectedModel');
            }
            
            const requestData = {
              messages,
              temperature: 0.7,
              max_tokens: 2000,
              images: images.map(image => image.url),
              modelType: 'vision'
            };
            
            jsonResponse = await generateContent(modelIdentifier, requestData);
            
          } else {
            throw new Error(`Unsupported provider for image analysis: ${selectedModel.provider}`);
          }
          
          // Get the content from the response and try to parse it as JSON
          let parsedResponse = {};
          const content = jsonResponse.choices && jsonResponse.choices[0] && jsonResponse.choices[0].message && jsonResponse.choices[0].message.content;
          
          // CRITICAL DEBUGGING: Show the message content for troubleshooting
          console.log('RAW MESSAGE CONTENT FROM API:', content);
          
          if (content) {
            try {
              // First, clean the content by removing any code block markers
              const cleanedContent = content.replace(/```json|```/g, '').trim();
              parsedResponse = JSON.parse(cleanedContent);
              console.log('Successfully parsed JSON response:', Object.keys(parsedResponse));
            } catch (jsonError) {
              console.warn('Failed to parse cleaned JSON, trying to extract JSON from content:', jsonError.message);
              
              // Try to extract JSON from the content
              try {
                // Look for JSON objects in the content
                const jsonMatch = content.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                  parsedResponse = JSON.parse(jsonMatch[0]);
                  console.log('Successfully extracted and parsed JSON from content:', Object.keys(parsedResponse));
                } else {
                  console.warn('Could not extract JSON from content, falling back to raw content');
                  // For non-JSON responses, create a simple object with the content
                  parsedResponse = { description: content };
                }
              } catch (extractError) {
                console.warn('Failed to extract JSON from content:', extractError.message);
                // For non-JSON responses, create a simple object with the content
                parsedResponse = { description: content };
              }
            }
          }
          
          // Create a result object
          const result = {
            id: product.id,
            productName: product.title || `Product ${i + 1}`,
            templateName: template.name,
            imageUrl: images[0].url,
            status: 'success',
            fields: {},
            insertModes: {}
          };
          
          console.log('Template sidebar only:', template.sidebarOnly);
          
          // Get the content from the API response
          const generatedContent = parsedResponse.description || 
            (Object.keys(parsedResponse).length > 0 ? parsedResponse[Object.keys(parsedResponse)[0]] : null) || 
            content;
          
          // Make sure generatedContent is a string to avoid 'substring is not a function' error
          let safeContent = '';
          
          // Format JSON content for readability if it's not already a string
          if (typeof generatedContent === 'string') {
            // If it looks like JSON but is actually a string, try to parse and format it
            if (generatedContent.trim().startsWith('{') && generatedContent.trim().endsWith('}')) {
              try {
                const jsonObj = JSON.parse(generatedContent);
                safeContent = formatJsonForDescription(jsonObj);
              } catch (e) {
                // Not valid JSON, use as is
                safeContent = generatedContent;
              }
            } else {
              safeContent = generatedContent;
            }
          } else if (typeof generatedContent === 'object' && generatedContent !== null) {
            // Format JSON object for human readability
            safeContent = formatJsonForDescription(generatedContent);
          } else {
            // Fallback to JSON.stringify for other types
            safeContent = JSON.stringify(generatedContent);
          }
          
          result.fields.description = safeContent;
          
          if (!template.sidebarOnly) {
            // For description mode, we update the product description
            console.log('Using description mode, updating product description');
            
            // Update the product description - APPEND to existing content, not replace
            if (onUpdate) {
              console.log(`Updating product ${product.id} description with content:`, 
                typeof safeContent === 'string' ? safeContent.substring(0, 50) + '...' : 'Non-string content');
              
              // Get existing description if available
              let existingDescription = '';
              if (product.body_html) {
                existingDescription = product.body_html.trim();
                console.log('Existing description found:', existingDescription.substring(0, 50) + '...');
              } else {
                console.log('No existing description found');
              }
              
              // Append the new content to the existing description with clear separation
              const updatedDescription = existingDescription ? 
                `${existingDescription}<br><hr><br>${safeContent}` : safeContent;
              
              console.log('Appending content to description, not replacing it:', 
                updatedDescription.substring(0, 50) + '...');
              
              // Ensure we're passing the full updated description with appended content
              // Simply pass the body_html content to the parent component
              // The parent component (TemplateTools) will handle the appending logic
              console.log('Passing body_html content to parent component for appending');
              onUpdate(product.id, {
                body_html: safeContent
              });
              
              console.log('Product description updated with appended content');
            }
          } else {
            // For sidebar-only mode, we just show the raw content in the sidebar
            console.log('Using sidebar-only mode, not updating any product fields');
          }
          
          // Add the result to the list
          newResults.push(result);
          
        } catch (productError) {
          console.error(`Error processing product ${product.id}:`, productError);
          
          // Add error result
          newResults.push({
            id: product.id,
            productName: product.title || `Product ${i + 1}`,
            templateName: template.name,
            imageUrl: images ? images[0].url : 'unknown',
            status: 'error',
            error: productError.message,
            fields: {}
          });
        }
        
        // Report progress to parent component
        if (onProgress) {
          // Calculate progress and ensure it never exceeds 100%
          const progressPercent = Math.min(((i + 1) / totalProducts) * 100, 100);
          console.log(`Image template progress: ${i + 1}/${totalProducts} = ${progressPercent.toFixed(1)}%`);
          onProgress(progressPercent);
        }
      }
      
      console.log('Processing complete, results:', newResults);
      
      // Directly save results to the parent component's sidebar
      await saveResultsToSidebar(newResults);
      
      // CRITICAL: Signal completion to the parent component with exactly 100% progress
      if (onProgress) {
        console.log('Setting final progress to exactly 100% to ensure loading indicator completes');
        // Force progress to exactly 100% to ensure the loading indicator completes
        onProgress(100);
        
        // Send the 100% progress signal again after a short delay to ensure it's received
        setTimeout(() => {
          console.log('Sending final 100% progress signal again after delay');
          onProgress(100);
        }, 500);
      }
      
      // Return the results
      return newResults;
    } catch (error) {
      console.error('Error processing images:', error);
      setError(`Error processing images: ${error.message}`);
      // Processing state is now handled by the parent component
      return [];
    }
  };

  // Helper function to format JSON for human-readable description
  const formatJsonForDescription = (jsonObj) => {
    let formattedContent = '';
    
    try {
      // Handle arrays specially
      if (Array.isArray(jsonObj)) {
        return jsonObj.map(item => {
          if (typeof item === 'object' && item !== null) {
            return formatJsonForDescription(item);
          }
          return item;
        }).join('<br>• ');
      }
      
      // Format each key-value pair in the JSON object
      Object.entries(jsonObj).forEach(([key, value]) => {
        // Skip empty values
        if (value === null || value === undefined || value === '') return;
        
        // Format the key for display
        const formattedKey = key
          .replace(/([A-Z])/g, ' $1') // Add spaces before capital letters
          .replace(/_/g, ' ')        // Replace underscores with spaces
          .trim()                    // Remove leading/trailing spaces
          .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
        
        // Handle different value types
        if (Array.isArray(value)) {
          // Format arrays as bullet points
          formattedContent += `<strong>${formattedKey}:</strong><br>• ${value.join('<br>• ')}<br><br>`;
        } else if (typeof value === 'object' && value !== null) {
          // Recursively format nested objects
          formattedContent += `<strong>${formattedKey}:</strong><br>${formatJsonForDescription(value)}<br>`;
        } else {
          // Simple key-value pair
          formattedContent += `<strong>${formattedKey}:</strong> ${value}<br><br>`;
        }
      });
      
      return formattedContent;
    } catch (e) {
      console.error('Error formatting JSON:', e);
      return JSON.stringify(jsonObj);
    }
  };

  // After processing images, directly save results to the parent component's sidebar
  const saveResultsToSidebar = async (results) => {
    try {
      console.log('Directly saving results to persistent sidebar:', results);
      
      // Only save to sidebar if sidebarOnly is true or there was an error
      // This ensures the sidebar doesn't open for 'Add to Description' mode unless there's an error
      if (onSaveResults && (template.sidebarOnly === true || results.some(r => r.status === 'error'))) {
        console.log('Saving results to sidebar because template.sidebarOnly =', template.sidebarOnly === true);
        await onSaveResults(results);
      } else {
        console.log('Skipping sidebar save because template.sidebarOnly =', Boolean(template.sidebarOnly));
      }
    } catch (error) {
      console.error('Error saving results to sidebar:', error);
      setError(`Error saving results: ${error.message}`);
    }
  };

  return (
    <Stack vertical spacing="tight">
      {error && (
        <Banner status="critical">
          <p>{error}</p>
        </Banner>
      )}
      
      <Card sectioned>
        <TextContainer>
          <Heading>Image Analyzing Template: {template?.name}</Heading>
          <Text variant="bodyMd">
            {useFeaturedImages ? 
              'Using featured images for all products. The AI will analyze these images automatically.' :
              'Select an image for each product to analyze with the AI model. The AI will generate content based on the selected images.'}
          </Text>
          
          {!useFeaturedImages && (
            <Banner status="info">
              <p><strong>Workflow Execution Note:</strong> Manual image templates (without "Use featured images automatically" enabled) will always be processed first in a workflow, regardless of their position in the template order.</p>
              <p>If you want image templates to run automatically in the exact order specified, enable the "Use featured images automatically" option.</p>
            </Banner>
          )}
          
          <Stack distribution="equalSpacing" alignment="center" spacing="tight" wrap={false}>
            <Stack.Item>
              <Checkbox
                label="Use featured images automatically"
                checked={useFeaturedImages}
                onChange={(checked) => {
                  setUseFeaturedImages(checked);
                  if (checked) {
                    selectFeaturedImages();
                  }
                }}
              />
            </Stack.Item>
          </Stack>
        </TextContainer>
      </Card>
      
      {/* Only show image selection UI when not using featured images */}
      {!useFeaturedImages && products.map(product => (
        <Card key={product.id} sectioned>
          <Stack vertical spacing="tight">
            <Heading>{product.title || 'Product'}</Heading>
            <ImageSelector
              product={product}
              selectedImages={selectedImages[product.id]}
              onSelectImage={(image) => handleSelectImage(product.id, image)}
              onSelectImages={(images) => handleSelectImages(product.id, images)}
              maxImages={selectedModel?.maxImages || (selectedModel?.provider === 'gemini' ? 3 : 1)}
            />
          </Stack>
        </Card>
      ))}
      
      {/* Only show the process button if not using featured images */}
      {(!template?.useFeaturedImage || !useFeaturedImages) && (
        <Card>
          <Card.Section>
            <Stack distribution="equalSpacing" alignment="center">
              <Stack.Item>
                {/* Processing UI is now handled by the parent component */}
              </Stack.Item>
              <Stack.Item>
                <ButtonGroup>
                  <Button onClick={() => onClose && onClose()}>Cancel</Button>
                  <Button
                    primary
                    onClick={processImages}
                    disabled={!allProductsHaveImages()}
                  >
                    Process Images
                  </Button>
                </ButtonGroup>
              </Stack.Item>
            </Stack>
          </Card.Section>
        </Card>
      )}
      
      {/* No processing indicators in the modal - processing UI is handled by parent component */}
      
      {/* Results are now managed by the parent component's persistent sidebar */}
    </Stack>
  );
};

export default ImageTemplateExecutor;
